import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, Check, X, Sparkles, ArrowRight } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

/**
 * SignupWallPage — iter189
 *
 * Full-page wall shown by <AnonymousGate> whenever an anonymous visitor
 * hits a signup-only route (browse, compare, favorites, DMs, chatroom,
 * profile, checklist, fisheries).
 *
 * Deliberately blunt — the whole point is to convert. Keeps the header
 * and footer so the site still feels like the site, but the middle is
 * one big "sign up to unlock" pitch with a clear FREE tier vs SIGNED-IN
 * tier list.
 *
 * We word it "free while we're growing" (not "free forever") because the
 * user is planning to introduce paid tiers later.
 */
const LOCKED_LABEL = {
  "/browse": "300 free campsites rotate every Monday — sign up to unlock all 22,000+",
  "/compare": "Compare campsites side-by-side",
  "/favorites": "Save & sync favourites across devices",
  "/profile": "Your camper profile",
  "/messages": "Direct-message other campers",
  "/chatroom": "Campfire Chat live room",
  "/checklist": "The interactive packing checklist",
  "/fisheries": "Every fishing spot next to a UK pitch",
};

export default function SignupWallPage() {
  const location = useLocation();
  const path = location.pathname + location.search;
  const registerHref = `/register?next=${encodeURIComponent(path)}`;
  const loginHref = `/login?next=${encodeURIComponent(path)}`;
  const label = LOCKED_LABEL[location.pathname] || "This feature";

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="signup-wall-page">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b hairline-strong bg-gradient-to-br from-[#ff4500] via-[#e03d00] to-[#a82e00] text-white">
          <div className="absolute inset-0 opacity-15" style={{
            backgroundImage: "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-14 lg:py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 text-[11px] mono uppercase tracking-widest font-black mb-4" data-testid="signup-wall-eyebrow">
              <Lock className="w-3 h-3" /> Members only · free while we grow
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight" data-testid="signup-wall-title">
              {label} — one free tap away.
            </h1>
            <p className="mt-5 text-base lg:text-lg leading-snug text-white/95 max-w-xl mx-auto">
              We're keeping CampCompass UK free while we grow. Create your free
              camper account (60 seconds, no card) to unlock the full app.
              Paid tiers arrive later this week — early sign-ups keep every
              feature included.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={registerHref}
                className="bg-white text-[#ff4500] font-black uppercase tracking-widest text-sm px-6 py-3.5 inline-flex items-center gap-2 shadow-[0_4px_0_0_#111] hover:shadow-[0_2px_0_0_#111] hover:translate-y-[2px] transition-all"
                data-testid="signup-wall-cta"
              >
                Sign up free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={loginHref}
                className="border-2 border-white text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/10"
                data-testid="signup-wall-login"
              >
                Already a member? Sign in
              </Link>
            </div>
          </div>
        </section>

        {/* Comparison — Free (not signed in) vs Signed-in */}
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
          <div className="text-center mb-10">
            <div className="eyebrow mb-2">The two tiers today</div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">
              What you get without an account · what unlocks when you sign up
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="signup-wall-comparison">
            {/* Anonymous tier */}
            <div className="border-2 hairline bg-white p-6 sm:p-7" data-testid="signup-wall-anon-tier">
              <div className="eyebrow mb-1 flex items-center gap-1.5">
                <span>Not signed in</span>
                <Lock className="w-3 h-3 text-[#888]" />
              </div>
              <div className="font-display text-2xl mb-4">Basic browse</div>
              <ul className="space-y-2.5 text-sm">
                {[
                  "See the trip wall",
                  "Read Campfire community threads + comments",
                  "See today's 5 most-viewed campsites",
                  "View campsite pages (map + prices locked)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#0a7f3f] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{t}</span>
                  </li>
                ))}
                {[
                  "300 rotating free every Monday · full 22,000+ with signup",
                  "See prices, facilities & map",
                  "Save favourites",
                  "Chat room + DMs",
                  "Book direct affiliate links",
                  "Weather + price alerts",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-[#999] line-through decoration-[#ccc]">
                    <X className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Signed-in tier */}
            <div className="border-2 border-[#ff4500] bg-[#fff9f4] p-6 sm:p-7 shadow-[0_6px_0_0_#111]" data-testid="signup-wall-member-tier">
              <div className="eyebrow mb-1 flex items-center gap-1.5 text-[#ff4500]">
                <Sparkles className="w-3 h-3" />
                <span>Signed in · free while we grow</span>
              </div>
              <div className="font-display text-2xl mb-4">Full app access</div>
              <ul className="space-y-2.5 text-sm">
                {[
                  "Everything in Basic browse",
                  "All 22,000+ UK campsites (anons get 300 rotating weekly)",
                  "See real prices, facilities & full map",
                  "Save unlimited favourites, synced across devices",
                  "Compare pitches side-by-side",
                  "Share your own trip stories + photos",
                  "Campfire Chat live room + private DMs",
                  "Weather + price alerts on saved sites",
                  "Book direct through affiliate deals",
                  "Every future feature we ship — included today",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={registerHref}
                className="mt-6 w-full inline-flex items-center justify-center bg-[#ff4500] text-white font-black text-sm uppercase tracking-widest px-4 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
                data-testid="signup-wall-cta-bottom"
              >
                Create free account
              </Link>
              <p className="text-[11px] text-center text-[#666] mt-2">
                No credit card · unsubscribe anytime · 60 seconds
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
